import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import apiClient from '@/lib/apiClient';

export type KpiSpotlightKpi =
  | 'deductions_this_week'
  | 'overdue_invoices'
  | 'invoices_due_this_week'
  | 'approved_this_week';

export type KpiSpotlightSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface KpiTimeframe {
  startDate: string;
  endDate: string;
}

export interface KpiSpotlightItem {
  kpi: KpiSpotlightKpi;
  severity: KpiSpotlightSeverity;
  currency: string | null;
  value: {
    amount: number;
    count: number;
  };
  comparison: {
    amount: number;
    changeRatio: number | null;
    timeframe: KpiTimeframe;
  } | null;
  primaryTimeframe: KpiTimeframe;
  ctaDeepLink: string | null;
  dataAsOf: string | null;
}

export interface KpiSpotlightData {
  makerOrganizationUuid: string;
  currency: string;
  // null means none of the four KPIs qualify for this buyer — callers fall back to the static banner.
  spotlight: KpiSpotlightItem | null;
}

export interface KpiSpotlightParams {
  makerOrganizationUuid: string;
  currency: string;
  targetCurrency?: string;
}

const fetchKpiSpotlight = async (params: KpiSpotlightParams): Promise<KpiSpotlightData> => {
  // For now, always mirror `currency` into `targetCurrency` (no conversion) — revisit once the UI
  // supports picking a different display currency than the invoices are denominated in.
  const queryString = qs.stringify({ ...params, targetCurrency: params.targetCurrency ?? params.currency });
  const response = await apiClient
    .get(`api/invoice-hub/v1/kpi-spotlight?${queryString}`)
    .json<{ data: KpiSpotlightData }>();
  return response.data;
};

export const useKpiSpotlight = (params: KpiSpotlightParams) =>
  useSuspenseQuery({
    queryKey: ['kpi-spotlight', params],
    queryFn: () => fetchKpiSpotlight(params),
    staleTime: 1000 * 60 * 60 * 24,
  });
